import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Copy, Trash2, Edit2, Plus, X, Power, Save,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARCHETYPE_LABEL = { leon: "LEÓN", lobo: "LOBO", halcon: "HALCÓN", delfin: "DELFÍN" };
const ARCHETYPE_COLOR = { leon: "#FF6B4A", lobo: "#A78BFA", halcon: "#29BC9B", delfin: "#5BC0EB" };

export default function EstrategiasPanel({ password }) {
  const [loading, setLoading] = useState(true);
  const [sequences, setSequences] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeArch, setActiveArch] = useState("leon");
  const [editing, setEditing] = useState(null); // {archetype, email}

  const headers = { "X-Admin-Password": password };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sequences`, { headers });
      setSequences(data.sequences || []);
      setStats(data.stats);
    } catch (e) {
      toast.error("Error cargando secuencias");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const current = sequences.find((s) => s.archetype === activeArch);

  const toggleActive = async (archetype, active) => {
    try {
      await axios.put(`${API}/admin/sequences/${archetype}`, { active }, { headers });
      toast.success(active ? "Secuencia activada" : "Secuencia desactivada");
      load();
    } catch {
      toast.error("Error actualizando");
    }
  };

  const saveEmail = async (archetype, email_id, payload) => {
    try {
      await axios.put(`${API}/admin/sequences/${archetype}/emails/${email_id}`, payload, { headers });
      toast.success("Email guardado");
      setEditing(null);
      load();
    } catch {
      toast.error("Error guardando");
    }
  };

  const duplicate = async (archetype, email_id) => {
    try {
      await axios.post(`${API}/admin/sequences/${archetype}/emails/${email_id}/duplicate`, {}, { headers });
      toast.success("Email duplicado");
      load();
    } catch {
      toast.error("Error duplicando");
    }
  };

  const remove = async (archetype, email_id) => {
    if (!window.confirm("¿Eliminar este email?")) return;
    try {
      await axios.delete(`${API}/admin/sequences/${archetype}/emails/${email_id}`, { headers });
      toast.success("Email eliminado");
      load();
    } catch {
      toast.error("Error eliminando");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#29BC9B]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="estrategias-panel">
      {/* Stats overview */}
      {stats && (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Enrolados" value={stats.total_enrolled} />
          <StatCard label="Aperturas" value={stats.total_opens} />
          <StatCard label="Clicks" value={stats.total_clicks} />
          <StatCard label="CTR" value={`${stats.ctr}%`} />
          <StatCard label="Bajas" value={stats.unsubscribed} />
        </section>
      )}

      {/* Archetype tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {Object.keys(ARCHETYPE_LABEL).map((a) => {
          const s = sequences.find((x) => x.archetype === a);
          const enrolled = stats?.per_archetype?.[a]?.enrolled ?? 0;
          return (
            <button
              key={a}
              data-testid={`seq-tab-${a}`}
              onClick={() => setActiveArch(a)}
              className={`flex items-center gap-3 px-4 py-2 rounded-sm text-xs uppercase tracking-[0.2em] border transition-colors ${
                activeArch === a
                  ? "border-[#29BC9B] text-[#29BC9B] bg-[#29BC9B]/5"
                  : "border-white/10 text-white/60 hover:border-white/30"
              }`}
            >
              <span style={{ color: ARCHETYPE_COLOR[a] }}>●</span>
              {ARCHETYPE_LABEL[a]}
              <span className="text-white/40 normal-case tracking-normal">· {enrolled} usuarios</span>
              {s && !s.active && <span className="text-red-400/70 normal-case">inactivo</span>}
            </button>
          );
        })}
      </div>

      {current && (
        <div className="space-y-6">
          {/* Sequence header */}
          <div className="flex items-center justify-between p-5 rounded-sm border border-white/10 bg-[#0A0A0A]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#29BC9B]">Flujo</div>
              <h2 className="font-display text-2xl text-white mt-1">{current.name}</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/50">Activo</span>
              <Switch
                data-testid={`seq-toggle-${current.archetype}`}
                checked={current.active}
                onCheckedChange={(v) => toggleActive(current.archetype, v)}
              />
            </div>
          </div>

          {/* Emails list */}
          <div className="space-y-3">
            {current.emails
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((e) => (
                <EmailRow
                  key={e.id}
                  email={e}
                  archetype={current.archetype}
                  onEdit={() => setEditing({ archetype: current.archetype, email: e })}
                  onDuplicate={() => duplicate(current.archetype, e.id)}
                  onDelete={() => remove(current.archetype, e.id)}
                />
              ))}
          </div>

          <p className="text-[10px] uppercase tracking-[0.25em] text-white/30 text-center mt-6">
            Variables disponibles: <span className="text-[#29BC9B]/70">{"{{nombre}}, {{avatar_dominante}}, {{avatar_secundario}}, {{modo_sombra}}, {{share_url}}"}</span>
          </p>
        </div>
      )}

      {editing && (
        <EmailEditor
          archetype={editing.archetype}
          email={editing.email}
          onClose={() => setEditing(null)}
          onSave={(payload) => saveEmail(editing.archetype, editing.email.id, payload)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="p-4 rounded-sm border border-white/8 bg-[#0A0A0A]">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{label}</div>
      <div className="font-display text-2xl text-white">{value}</div>
    </div>
  );
}

function EmailRow({ email, archetype, onEdit, onDuplicate, onDelete }) {
  return (
    <div
      className="flex items-start gap-4 p-5 rounded-sm border border-white/8 bg-[#0A0A0A] hover:border-white/20 transition-colors"
      data-testid={`email-row-${archetype}-${email.id}`}
    >
      <div className="w-10 flex-shrink-0 text-center">
        <div className="font-display text-2xl text-[#29BC9B]">{email.order}</div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1">
          {email.delay_days === 0 ? "Hoy" : `+${email.delay_days}d`}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium truncate">{email.subject}</div>
        <div className="text-xs text-white/40 truncate mt-1">{email.preview_text}</div>
        {!email.active && (
          <div className="mt-2 inline-block text-[9px] uppercase tracking-[0.2em] text-red-400/80">inactivo</div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={onEdit} data-testid={`email-edit-${email.id}`} className="p-2 rounded-sm hover:bg-white/5 text-white/60 hover:text-white" title="Editar">
          <Edit2 className="w-4 h-4" />
        </button>
        <button onClick={onDuplicate} data-testid={`email-duplicate-${email.id}`} className="p-2 rounded-sm hover:bg-white/5 text-white/60 hover:text-white" title="Duplicar">
          <Copy className="w-4 h-4" />
        </button>
        <button onClick={onDelete} data-testid={`email-delete-${email.id}`} className="p-2 rounded-sm hover:bg-red-500/10 text-white/40 hover:text-red-400" title="Eliminar">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function EmailEditor({ archetype, email, onClose, onSave }) {
  const [subject, setSubject] = useState(email.subject || "");
  const [preview, setPreview] = useState(email.preview_text || "");
  const [body, setBody] = useState(email.body || "");
  const [ctaText, setCtaText] = useState(email.cta_text || "");
  const [ctaUrl, setCtaUrl] = useState(email.cta_url || "");
  const [delay, setDelay] = useState(email.delay_days ?? 3);
  const [active, setActive] = useState(email.active !== false);

  const submit = (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      toast.error("El asunto es obligatorio");
      return;
    }
    onSave({
      subject, preview_text: preview, body, cta_text: ctaText, cta_url: ctaUrl,
      delay_days: Number(delay) || 0, active,
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#070707] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Editar email {email.order} · {ARCHETYPE_LABEL[archetype]}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Configura asunto, cuerpo, CTA y tiempos de envío. Soporta Markdown y variables.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-4" data-testid="email-editor-form">
          <Field label="Asunto"><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ed-subject" className="bg-[#050505] border-white/10 text-white" /></Field>
          <Field label="Preview text (lo que se ve en la bandeja)"><Input value={preview} onChange={(e) => setPreview(e.target.value)} data-testid="ed-preview" className="bg-[#050505] border-white/10 text-white" /></Field>
          <Field label="Cuerpo (Markdown: **negrita**, salto de línea, [texto](url))">
            <textarea
              data-testid="ed-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="w-full bg-[#050505] border border-white/10 rounded-sm p-3 text-sm text-white font-mono leading-relaxed focus:outline-none focus:border-[#29BC9B]"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Texto del botón"><Input value={ctaText} onChange={(e) => setCtaText(e.target.value)} data-testid="ed-cta-text" className="bg-[#050505] border-white/10 text-white" /></Field>
            <Field label="URL del botón (admite {{share_url}})"><Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} data-testid="ed-cta-url" className="bg-[#050505] border-white/10 text-white" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Días de espera tras el anterior"><Input type="number" min={0} value={delay} onChange={(e) => setDelay(e.target.value)} data-testid="ed-delay" className="bg-[#050505] border-white/10 text-white" /></Field>
            <div className="flex items-end gap-3 pb-2">
              <Switch checked={active} onCheckedChange={setActive} data-testid="ed-active" />
              <span className="text-sm text-white/70">{active ? "Activo" : "Inactivo"}</span>
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4 border-t border-white/8">
            <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-4 !text-[10px]">
              <X className="w-3.5 h-3.5" /> Cancelar
            </button>
            <button type="submit" data-testid="ed-save" className="btn-teal !py-2 !px-4 !text-[10px]">
              <Save className="w-3.5 h-3.5" /> Guardar
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/60">{label}</Label>
      {children}
    </div>
  );
}
